/**
 * Data layer for ask-the-campaign chat threads (#192 follow-up).
 *
 * Threads and their turns live in the `ask_threads` / `ask_messages` tables and
 * are read/written directly through the authenticated browser Supabase client
 * (RLS gates access to the team that can reach the parent campaign, matching the
 * ask-campaign endpoint's membership gate). This replaces the previous
 * localStorage-only persistence so a thread is durable and shared across the
 * team rather than stuck on one browser.
 *
 * Every function takes the client as its first argument so it can be unit-tested
 * with a fake, mirroring the `functions/api/gemini/_case-study-db` style.
 *
 * @module services/askThreadService
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Citation, AskToolCall } from './geminiService';
import { readChartPayload } from '../lib/campaign-analytics/payload';

/** A thread as listed in the "previous conversations" switcher. */
export interface AskThreadSummary {
  /** Thread id. */
  id: string;
  /** Short label (derived from the first question); null when unset. */
  title: string | null;
  /** ISO timestamp of the thread's last activity, for ordering/display. */
  updatedAt: string;
}

/** One stored turn of a thread. */
export interface AskThreadMessage {
  /** Row id. */
  id: string;
  /** Who produced the turn. */
  role: 'user' | 'assistant';
  /** The turn text (assistant text may carry inline `[C<id>:T<idx>]` labels). */
  content: string;
  /** Grounded citations for an assistant turn (empty for a user turn). */
  citations: Citation[];
  /** The agent's reasoning for an assistant turn, when surfaced. */
  thinking?: string;
  /** The agent's tool-call trace for an assistant turn (empty for a user turn). */
  toolCalls?: AskToolCall[];
  /**
   * The member who wrote a user turn (auth.uid() at insert), or null. Used to
   * attribute turns in a team-shared thread; an assistant turn is always hollie.
   */
  authorId: string | null;
  /** ISO timestamp the turn was written, for the per-message timestamp. */
  createdAt: string;
}

/**
 * Raw shape of an `ask_messages` row, whether read by a `select` or delivered by
 * a realtime postgres_changes INSERT event. This is the single source of truth
 * for the column set, so the initial-load path and the live realtime path map a
 * row through one function and never drift when a column is added or renamed.
 */
export interface AskMessageRow {
  /** Row id. */
  id: string;
  /** Parent thread id (used to guard a late event after a thread switch). */
  thread_id: string;
  /** Who produced the turn. */
  role: 'user' | 'assistant';
  /** The turn text. */
  content: string;
  /** Grounded citations for an assistant turn; null when unset. */
  citations: Citation[] | null;
  /** The agent's reasoning for an assistant turn; null when unset. */
  thinking: string | null;
  /** The agent's tool-call trace for an assistant turn; null when unset. */
  tool_trace: AskToolCall[] | null;
  /** The member who wrote a user turn (auth.uid() at insert); null otherwise. */
  created_by: string | null;
  /** ISO timestamp the turn was written. */
  created_at: string;
}

/**
 * Map one raw `ask_messages` row to an {@link AskThreadMessage}, or null when the
 * row is missing or unusable (no id). Shared by {@link loadAskThreadMessages} and
 * the component's realtime handler so a column change is applied in exactly one
 * place. The result is a superset-compatible value for the component's
 * `ChatMessage` (optimistic/streaming) type.
 */
export function mapAskMessageRow(row: AskMessageRow | null | undefined): AskThreadMessage | null {
  if (!row || !row.id) return null;
  // Stored traces are data: a chart card is re-validated before it can render.
  const toolCalls = (Array.isArray(row.tool_trace) ? row.tool_trace : []).map(({ chart, ...call }) => {
    const valid = chart === undefined ? null : readChartPayload(chart);
    return valid ? { ...call, chart: valid } : call;
  });
  return {
    id: row.id,
    role: row.role,
    content: row.content,
    citations: Array.isArray(row.citations) ? row.citations : [],
    authorId: row.created_by ?? null,
    createdAt: row.created_at,
    ...(row.thinking ? { thinking: row.thinking } : {}),
    ...(toolCalls.length ? { toolCalls } : {}),
  };
}

/** Fields needed to open a new thread. */
export interface CreateAskThreadInput {
  /** Parent campaign (interview session) id. */
  interviewId: string;
  /** Scope partition key (see {@link askThreadScopeKey}). */
  scopeKey: string;
  /** The conversation subset this thread is scoped to (omit for whole campaign). */
  interviewDataIds?: string[] | null;
  /** Active organization id, denormalized onto the row (not an authz key). */
  organizationId?: string | null;
  /** Short label derived from the first question. */
  title?: string | null;
}

/**
 * Canonical, order-independent key partitioning threads by the surface that
 * created them: `'all'` for a whole-campaign ask, or the sorted comma-joined
 * conversation ids for a single-conversation ask. The whole-campaign and the
 * single-conversation surfaces therefore list/switch within their own scope.
 */
export function askThreadScopeKey(interviewDataIds?: string[] | null): string {
  if (!interviewDataIds || interviewDataIds.length === 0) {
    return 'all';
  }
  return [...interviewDataIds].sort().join(',');
}

/** List a campaign+scope's threads, newest activity first. */
export async function listAskThreads(
  supabase: SupabaseClient,
  interviewId: string,
  scopeKey: string,
): Promise<AskThreadSummary[]> {
  const { data, error } = await supabase
    .from('ask_threads')
    .select('id, title, updated_at')
    .eq('interview_session_id', interviewId)
    .eq('scope_key', scopeKey)
    .order('updated_at', { ascending: false });

  if (error) {
    throw new Error(error.message || 'Failed to load conversations');
  }

  return ((data ?? []) as Array<{ id: string; title: string | null; updated_at: string }>).map((row) => ({
    id: row.id,
    title: row.title ?? null,
    updatedAt: row.updated_at,
  }));
}

/** Load a thread's turns, oldest first. */
export async function loadAskThreadMessages(
  supabase: SupabaseClient,
  threadId: string,
): Promise<AskThreadMessage[]> {
  const { data, error } = await supabase
    .from('ask_messages')
    .select('id, role, content, citations, thinking, tool_trace, created_by, created_at')
    .eq('thread_id', threadId)
    .order('created_at', { ascending: true });

  if (error) {
    throw new Error(error.message || 'Failed to load conversation');
  }

  return ((data ?? []) as AskMessageRow[])
    .map(mapAskMessageRow)
    .filter((message): message is AskThreadMessage => message !== null);
}

/** Open a new thread and return its summary. */
export async function createAskThread(
  supabase: SupabaseClient,
  input: CreateAskThreadInput,
): Promise<AskThreadSummary> {
  const { data, error } = await supabase
    .from('ask_threads')
    .insert({
      interview_session_id: input.interviewId,
      scope_key: input.scopeKey,
      interview_data_ids:
        input.interviewDataIds && input.interviewDataIds.length > 0 ? input.interviewDataIds : null,
      organization_id: input.organizationId ?? null,
      title: input.title ?? null,
    })
    .select('id, title, updated_at')
    .single();

  if (error || !data) {
    throw new Error(error?.message || 'Failed to start conversation');
  }

  const row = data as { id: string; title: string | null; updated_at: string };
  return { id: row.id, title: row.title ?? null, updatedAt: row.updated_at };
}

/** Rename a thread (used to apply an AI-generated title to a new conversation). */
export async function updateAskThreadTitle(
  supabase: SupabaseClient,
  threadId: string,
  title: string,
): Promise<void> {
  const { error } = await supabase.from('ask_threads').update({ title }).eq('id', threadId);
  if (error) {
    throw new Error(error.message || 'Failed to rename conversation');
  }
}

/** Append a turn to a thread. */
export async function appendAskMessage(
  supabase: SupabaseClient,
  threadId: string,
  turn: {
    /**
     * Optional row id. Pass the same id the optimistic turn carries so the
     * realtime echo of our own insert can be deduped; omit to let the DB
     * generate one. The author (created_by) is always filled by the DB default
     * (auth.uid()) and is never sent from the client.
     */
    id?: string;
    role: 'user' | 'assistant';
    content: string;
    citations: Citation[];
    /** The agent's reasoning for an assistant turn. */
    thinking?: string;
    /** The agent's tool-call trace for an assistant turn. */
    toolCalls?: AskToolCall[];
  },
): Promise<void> {
  const toolTrace = (turn.toolCalls ?? []).map(({ name, args, ok, preview, csvExport, chart }) => ({
    name,
    ...(args ? { args } : {}),
    ...(ok !== undefined ? { ok } : {}),
    ...(preview ? { preview } : {}),
    ...(csvExport ? { csvExport } : {}),
    ...(chart ? { chart } : {}),
  }));
  const { error } = await supabase.from('ask_messages').insert({
    ...(turn.id ? { id: turn.id } : {}),
    thread_id: threadId,
    role: turn.role,
    content: turn.content,
    citations: turn.citations,
    thinking: turn.thinking ?? null,
    tool_trace: toolTrace,
  });

  if (error) {
    throw new Error(error.message || 'Failed to save message');
  }
}

/** Delete a thread (cascades to its turns), clearing it for the whole team. */
export async function deleteAskThread(supabase: SupabaseClient, threadId: string): Promise<void> {
  const { error } = await supabase.from('ask_threads').delete().eq('id', threadId);

  if (error) {
    throw new Error(error.message || 'Failed to delete conversation');
  }
}
